import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { unparse } from 'papaparse';
import {
  getLastNDaysDateRange,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
  getThisMonthDateRange,
} from 'src/utils/dateUtils';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TableSortLabel,
  TablePagination,
  Skeleton,
  Menu,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { useQuery, useMutation } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
  getLookbackThisMonthBlockedMessage,
} from 'src/utils/lookbackMessages';
import {
  GET_INVENTORY_DATA,
  GET_INVENTORY_COUNTRY_OPTIONS,
  UPDATE_INVENTORY_STATUS,
  SKIP_PRODUCT,
  GET_INVENTORY_THRESHOLD,
  UPDATE_INVENTORY_THRESHOLD,
  GENERATE_INVENTORY_AI_REPORT,
  GENERATE_INVENTORY_AI_REPORT_DATA,
} from 'src/utils/gql/inventoryQueries';

interface InventoryProduct {
  productId: string;
  productTitle: string;
  productType?: string;
  vendor?: string;
  tags?: string[];
  collections?: string[];
  imageUrl?: string;
  totalSold: number;
  totalRevenue: number;
  currentStock: number;
  dailySalesRate: number;
  daysToSoldOut?: number;
  isLowStock: boolean;
  todoStatus?: 'to_restock' | 'restocked';
  notes?: string;
  restockedDate?: string;
  restockedQuantity?: number;
  isSkipped?: boolean;
  markup?: number;
  score?: number;
  scoreColor?: string;
  // scoreM?: number; // Commentato: metrica marketing
  variants: InventoryVariant[];
  changes?: {
    dailySalesRate: {
      percentage: number;
      isPositive: boolean;
      previousValue: number;
    } | null;
    // currentStock non viene confrontato: è un valore istantaneo
    daysToSoldOut: {
      percentage: number;
      isPositive: boolean;
      previousValue: number;
    } | null;
    score: {
      percentage: number;
      isPositive: boolean;
      previousValue: number;
    } | null;
  } | null;
}

interface InventoryVariant {
  variantId: string;
  variantTitle: string;
  sku?: string;
  totalSold: number;
  currentStock: number;
  dailySalesRate: number;
  daysToSoldOut?: number;
  isLowStock: boolean;
}

type SortField =
  | 'productTitle'
  | 'dailySalesRate'
  | 'totalSold'
  | 'totalRevenue'
  | 'currentStock'
  | 'daysToSoldOut'
  | 'score'
  // | 'scoreM' // Commentato: metrica marketing
  | 'todoStatus';
type SortOrder = 'asc' | 'desc';

// Container responsive ottimizzato per tutti i dispositivi
const InventoryContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',

  // Mantiene il contenuto fuori dalla sidebar senza usare 100vw, che taglia il lato destro.
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    padding: 0,
    maxWidth: 'calc(100% - 240px)',
  },

  // Tablet medio: sidebar più stretta.
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    padding: 0,
    maxWidth: 'calc(100% - 220px)',
  },

  // Mobile: nessuna sidebar fissa, drawer overlay
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    padding: 0,
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

// Container interno completamente fluido e responsive
const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '16px',
  boxSizing: 'border-box',
  overflow: 'visible',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
}));

const insightGlowKeyframes = keyframes({
  '0%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
  '50%': {
    boxShadow: `0 4px 20px -4px ${palette.primary.main}40, 0 0 0 1px ${palette.primary.main}20`,
  },
  '100%': { boxShadow: '0 2px 12px -2px rgba(0,0,0,0.06)' },
});

/** Evita reset a «ultimi 30 giorni» dopo refresh / navigazione (stesso schema della pagina KPI). */
const INVENTORY_PERIOD_STORAGE_KEY = 'kipify.inventory.period.v1';

type InventoryPeriodPersisted = {
  dateRangePreset: string;
  dateRange?: { from: string; to: string };
  compareMode?: string;
  compareDateRange?: { from: string; to: string } | null;
};

function loadInventoryPeriodFromStorage(): InventoryPeriodPersisted | null {
  try {
    const raw = localStorage.getItem(INVENTORY_PERIOD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InventoryPeriodPersisted;
    if (!parsed || typeof parsed.dateRangePreset !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveInventoryPeriodToStorage(state: InventoryPeriodPersisted): void {
  try {
    localStorage.setItem(INVENTORY_PERIOD_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // best-effort
  }
}

function buildInitialInventoryPeriodState(): {
  dateRange: { from: string; to: string };
  dateRangePreset: string;
  showCustomDateRange: boolean;
  compareMode: string;
  compareDateRange: { from: string; to: string } | null;
  showCustomCompareDates: boolean;
} {
  const saved = loadInventoryPeriodFromStorage();
  const rawPreset = saved?.dateRangePreset ?? '30';
  const preset = ['7', '30', '90', 'this_month', 'custom'].includes(rawPreset)
    ? rawPreset
    : '30';
  let dateRange = getLastNDaysDateRange(30);
  let showCustomDateRange = false;

  if (preset === 'this_month') {
    dateRange = getThisMonthDateRange();
  } else if (preset !== 'custom') {
    const days = parseInt(preset, 10);
    if (!isNaN(days)) dateRange = getLastNDaysDateRange(days);
  } else {
    const dr = saved?.dateRange;
    if (dr?.from && dr?.to && dr.from <= dr.to) {
      dateRange = { from: dr.from, to: dr.to };
      showCustomDateRange = true;
    }
  }

  const rawCompareMode = saved?.compareMode ?? 'none';
  const compareMode =
    rawCompareMode === 'previous_period' ||
    rawCompareMode === 'previous_year' ||
    rawCompareMode === 'custom' ||
    rawCompareMode === 'none'
      ? rawCompareMode
      : 'none';

  let compareDateRange: { from: string; to: string } | null = null;
  let showCustomCompareDates = false;

  if (compareMode === 'previous_period') {
    compareDateRange = getPreviousPeriodDateRange(dateRange);
  } else if (compareMode === 'previous_year') {
    compareDateRange = getPreviousYearDateRange(dateRange);
  } else if (compareMode === 'custom') {
    const cr = saved?.compareDateRange;
    if (cr?.from && cr?.to && cr.from <= cr.to) {
      compareDateRange = { from: cr.from, to: cr.to };
      showCustomCompareDates = true;
    }
  }

  const effectiveCompareMode =
    compareMode === 'custom' && !compareDateRange ? 'none' : compareMode;

  return {
    dateRange,
    dateRangePreset: preset,
    showCustomDateRange,
    compareMode: effectiveCompareMode,
    compareDateRange: effectiveCompareMode === 'none' ? null : compareDateRange,
    showCustomCompareDates:
      effectiveCompareMode === 'custom' ? showCustomCompareDates : false,
  };
}

const Inventory: React.FC = () => {
  const theme = useTheme();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const snackbar = useSnackbar();
  const { currentPlan, aiCallsUsed, aiCallsLimit, lookbackDays } =
    useTrialStatus();
  const aiCallsRemaining = aiCallsLimit - aiCallsUsed;

  // Quando la sidebar è collapsed (72px), la tabella usa più spazio; quando aperta (240px) layout normale
  const containerSxWhenCollapsed = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set(),
  );
  const [editingProduct, setEditingProduct] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState<string>('');
  const [editStatus, setEditStatus] = useState<'to_restock' | 'restocked'>(
    'to_restock',
  );
  const [sortField, setSortField] = useState<SortField>('dailySalesRate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const invPeriodInitRef = React.useRef<ReturnType<
    typeof buildInitialInventoryPeriodState
  > | null>(null);
  if (!invPeriodInitRef.current) {
    invPeriodInitRef.current = buildInitialInventoryPeriodState();
  }
  const invPeriod0 = invPeriodInitRef.current;

  const isInventoryPeriodInitialMount = React.useRef(true);

  const [dateRange, setDateRange] = useState(() => invPeriod0.dateRange);
  const [dateRangePreset, setDateRangePreset] = useState<string>(
    () => invPeriod0.dateRangePreset,
  );
  const [showCustomDateRange, setShowCustomDateRange] = useState(
    () => invPeriod0.showCustomDateRange,
  );
  // Stato temporaneo per le date custom (non applicate finché non si clicca Apply)
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  // Stato per il confronto con periodo precedente
  const [compareMode, setCompareMode] = useState<string>(
    () => invPeriod0.compareMode,
  ); // 'none' | 'previous_period' | 'previous_year' | 'custom'
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(() => invPeriod0.compareDateRange);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(
    () => invPeriod0.showCustomCompareDates,
  );
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);

  // AI Report state
  const [aiReport, setAiReport] = useState<string | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Misurazione tempo fetch iniziale lato front
  const fetchTimerStarted = React.useRef(false);

  // Stato per la soglia configurabile
  const [threshold, setThreshold] = useState<number>(30);
  const [showThresholdDialog, setShowThresholdDialog] = useState(false);

  // Stato per la ricerca
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Stato per il filtro per status
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  // Stati per i nuovi filtri (array per selezione multipla)
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [collectionFilter, setCollectionFilter] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Conteggio filtri attivi per badge
  const activeFilterCount =
    countryFilter.length +
    (customerTypeFilter ? 1 : 0) +
    tagFilter.length +
    collectionFilter.length +
    vendorFilter.length +
    productTypeFilter.length;

  // Reset pagina quando cambiano ricerca o filtri query
  useEffect(() => {
    setPage(0);
  }, [searchTerm, countryFilter, customerTypeFilter]);

  // Aggiorna compareDateRange quando cambia dateRange (solo per previous_period e previous_year, non per custom)
  useEffect(() => {
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(dateRange));
    }
    // Per 'custom' non aggiorniamo automaticamente - l'utente ha scelto manualmente il range
  }, [dateRange, compareMode]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const canUseThisMonthPreset = useMemo(() => {
    if (!lookbackMinDate) return true;
    const thisMonthRange = getThisMonthDateRange();
    return thisMonthRange.from >= lookbackMinDate;
  }, [lookbackMinDate]);

  const previousPeriodRangeCandidate = useMemo(
    () => getPreviousPeriodDateRange(dateRange),
    [dateRange],
  );
  const previousYearRangeCandidate = useMemo(
    () => getPreviousYearDateRange(dateRange),
    [dateRange],
  );
  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  // Persistenza periodo / confronto (refresh o cambio pagina non resetta a 30 giorni)
  useEffect(() => {
    if (isInventoryPeriodInitialMount.current) {
      isInventoryPeriodInitialMount.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      saveInventoryPeriodToStorage({
        dateRangePreset,
        dateRange,
        compareMode,
        compareDateRange,
      });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [dateRange, dateRangePreset, compareMode, compareDateRange]);

  const { data, loading, error, refetch, networkStatus } = useQuery(
    GET_INVENTORY_DATA,
    {
      variables: {
        dateRange: {
          from: dateRange.from,
          to: dateRange.to,
        },
        ...(countryFilter?.length ? { countries: countryFilter } : {}),
        ...(customerTypeFilter ? { customerType: customerTypeFilter } : {}),
      },
      notifyOnNetworkStatusChange: false, // Disabilitato per evitare refetch automatici dalle mutation
      fetchPolicy: 'network-only', // forza il ricalcolo quando cambia il periodo (dateRange)
      nextFetchPolicy: 'cache-first', // Dopo il primo fetch, usa cache per evitare refetch automatici
      onCompleted: () => {
        if (fetchTimerStarted.current) {
          fetchTimerStarted.current = false;
        }
      },
      onError: () => {
        if (fetchTimerStarted.current) {
          fetchTimerStarted.current = false;
        }
      },
    },
  );

  const { data: inventoryCountryOptionsData } = useQuery(
    GET_INVENTORY_COUNTRY_OPTIONS,
    {
      fetchPolicy: 'cache-first',
    },
  );

  // Query per ottenere i dati del periodo di confronto
  const {
    data: compareData,
    loading: compareLoading,
    error: compareError,
  } = useQuery(GET_INVENTORY_DATA, {
    skip: !compareDateRange || compareMode === 'none',
    variables: {
      dateRange: {
        from: compareDateRange?.from || '',
        to: compareDateRange?.to || '',
      },
      ...(countryFilter?.length ? { countries: countryFilter } : {}),
      ...(customerTypeFilter ? { customerType: customerTypeFilter } : {}),
    },
    fetchPolicy: 'network-only',
  });

  // Reset del timer quando la query parte
  useEffect(() => {
    if (loading && !fetchTimerStarted.current) {
      fetchTimerStarted.current = true;
    }
  }, [loading]);

  // Nessun refetch manuale: l'hook si riallaccia automaticamente quando cambiano le variables

  const [updateInventoryStatus] = useMutation(UPDATE_INVENTORY_STATUS);
  const [restockDialogOpen, setRestockDialogOpen] = useState(false);
  const [restockTarget, setRestockTarget] = useState<InventoryProduct | null>(
    null,
  );
  const [restockQuantity, setRestockQuantity] = useState<string>('');
  const [restockDate, setRestockDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );

  const [skipProduct] = useMutation(SKIP_PRODUCT);
  const [updateInventoryThreshold] = useMutation(UPDATE_INVENTORY_THRESHOLD);
  const [generateAIReport] = useMutation(GENERATE_INVENTORY_AI_REPORT);
  const [generateAIReportData] = useMutation(GENERATE_INVENTORY_AI_REPORT_DATA);

  // Query per ottenere la soglia attuale
  const { data: thresholdData } = useQuery(GET_INVENTORY_THRESHOLD);

  // Aggiorna la soglia quando i dati arrivano
  useEffect(() => {
    if (thresholdData?.getInventoryThreshold) {
      setThreshold(thresholdData.getInventoryThreshold);
    }
  }, [thresholdData]);

  const inventoryData: InventoryProduct[] = data?.getInventoryData || [];
  const compareInventoryData: InventoryProduct[] =
    compareData?.getInventoryData || [];

  // Estrai valori unici per i filtri
  const availableTags = useMemo(() => {
    const tagsSet = new Set<string>();
    inventoryData.forEach((product) => {
      product.tags?.forEach((tag) => tagsSet.add(tag));
    });
    return Array.from(tagsSet).sort();
  }, [inventoryData]);

  const availableCollections = useMemo(() => {
    const collectionsSet = new Set<string>();
    inventoryData.forEach((product) => {
      product.collections?.forEach((collection) =>
        collectionsSet.add(collection),
      );
    });
    return Array.from(collectionsSet).sort();
  }, [inventoryData]);

  const availableVendors = useMemo(() => {
    const vendorsSet = new Set<string>();
    inventoryData.forEach((product) => {
      if (product.vendor) vendorsSet.add(product.vendor);
    });
    return Array.from(vendorsSet).sort();
  }, [inventoryData]);

  const availableProductTypes = useMemo(() => {
    const typesSet = new Set<string>();
    inventoryData.forEach((product) => {
      if (product.productType) typesSet.add(product.productType);
    });
    return Array.from(typesSet).sort();
  }, [inventoryData]);

  const availableCountries = useMemo(
    () => inventoryCountryOptionsData?.getInventoryCountryOptions ?? [],
    [inventoryCountryOptionsData],
  );

  // Calcola le variazioni per ogni prodotto
  const inventoryDataWithChanges = useMemo(() => {
    if (!compareDateRange || compareMode === 'none') {
      return inventoryData.map((product) => ({
        ...product,
        changes: null,
      }));
    }

    // Crea una mappa dei prodotti del periodo precedente per lookup veloce
    const compareMap = new Map<string, InventoryProduct>();
    compareInventoryData.forEach((product) => {
      compareMap.set(product.productId, product);
    });

    return inventoryData.map((product) => {
      const compareProduct = compareMap.get(product.productId);
      if (!compareProduct) {
        return { ...product, changes: null };
      }

      // Calcola variazioni per ogni metrica
      const calculateChange = (
        current: number,
        previous: number,
      ): {
        percentage: number;
        isPositive: boolean;
        previousValue: number;
      } | null => {
        if (previous === 0) {
          return {
            percentage: current > 0 ? 100 : 0,
            isPositive: current > 0,
            previousValue: previous,
          };
        }
        const percentage = ((current - previous) / previous) * 100;
        return {
          percentage: Math.round(percentage * 100) / 100,
          isPositive: percentage >= 0,
          previousValue: previous,
        };
      };

      return {
        ...product,
        changes: {
          dailySalesRate: calculateChange(
            product.dailySalesRate,
            compareProduct.dailySalesRate,
          ),
          // Stock non viene confrontato: è un valore istantaneo, non ha senso confrontarlo tra periodi
          currentStock: null,
          daysToSoldOut:
            product.daysToSoldOut && compareProduct.daysToSoldOut
              ? calculateChange(
                  product.daysToSoldOut,
                  compareProduct.daysToSoldOut,
                )
              : null,
          score:
            product.score && compareProduct.score
              ? calculateChange(product.score, compareProduct.score)
              : null,
        },
      };
    });
  }, [inventoryData, compareInventoryData, compareDateRange, compareMode]);

  /** Concentratione vendite + low stock: stesso linguaggio visivo di Products Analysis. */
  const inventoryKeyInsight = useMemo(() => {
    const rows = inventoryDataWithChanges.filter((p) => !p.isSkipped);
    if (rows.length === 0) return null;
    const totalSold = rows.reduce((s, p) => s + p.totalSold, 0);
    const bySold = [...rows].sort((a, b) => b.totalSold - a.totalSold);
    const topPct = 20;
    const k = Math.max(1, Math.ceil((rows.length * topPct) / 100));
    const topSold = bySold.slice(0, k).reduce((s, p) => s + p.totalSold, 0);
    const unitsPct =
      totalSold > 0 ? Math.round((topSold / totalSold) * 100) : 0;
    const lowCount = rows.filter((p) => p.isLowStock).length;
    return {
      unitsPct,
      topK: k,
      totalProducts: rows.length,
      lowCount,
      totalSold,
      topPct,
    };
  }, [inventoryDataWithChanges]);

  // Filtra i dati in base al termine di ricerca e status
  const filteredData = useMemo(() => {
    let filtered = inventoryDataWithChanges;

    // Filtro per ricerca
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (product) =>
          product.productTitle
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          product.variants.some(
            (variant) =>
              variant.variantTitle
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              (variant.sku &&
                variant.sku.toLowerCase().includes(searchTerm.toLowerCase())),
          ),
      );
    }

    // Filtro per status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((product) => {
        if (statusFilter === 'pending') {
          return (
            !product.isLowStock && !product.todoStatus && !product.isSkipped
          );
        }
        if (statusFilter === 'to_restock') {
          // Escludi prodotti skipped o restocked
          if (product.isSkipped || product.todoStatus === 'restocked') {
            return false;
          }
          // Includi solo prodotti low stock o con todoStatus 'to_restock'
          return product.isLowStock || product.todoStatus === 'to_restock';
        }
        if (statusFilter === 'restocked') {
          // Escludi prodotti skipped
          if (product.isSkipped) {
            return false;
          }
          return product.todoStatus === 'restocked';
        }
        if (statusFilter === 'skipped') {
          return product.isSkipped;
        }
        return true;
      });
    }

    // Filtro per tag (selezione multipla)
    if (tagFilter.length > 0) {
      filtered = filtered.filter((product) => {
        return product.tags?.some((tag) => tagFilter.includes(tag)) || false;
      });
    }

    // Filtro per collection (selezione multipla)
    if (collectionFilter.length > 0) {
      filtered = filtered.filter((product) => {
        return (
          product.collections?.some((collection) =>
            collectionFilter.includes(collection),
          ) || false
        );
      });
    }

    // Filtro per vendor (selezione multipla)
    if (vendorFilter.length > 0) {
      filtered = filtered.filter((product) => {
        return product.vendor && vendorFilter.includes(product.vendor);
      });
    }

    // Filtro per product type (selezione multipla)
    if (productTypeFilter.length > 0) {
      filtered = filtered.filter((product) => {
        return (
          product.productType && productTypeFilter.includes(product.productType)
        );
      });
    }

    return filtered;
  }, [
    inventoryDataWithChanges,
    searchTerm,
    statusFilter,
    tagFilter,
    collectionFilter,
    vendorFilter,
    productTypeFilter,
  ]);

  // Funzioni per gestire le azioni con update ottimistico della cache
  const handleUpdateStatus = async (
    productId: string,
    status: 'to_restock' | 'restocked' | null,
    restocked?: { date?: string; quantity?: number | null },
  ) => {
    try {
      await updateInventoryStatus({
        variables: {
          input: {
            productId,
            todoStatus: status === null ? 'pending' : status,
            notes: status === 'restocked' ? 'Marked as restocked' : undefined,
            ...(status === 'restocked'
              ? {
                  restockedDate: restocked?.date,
                  restockedQuantity: restocked?.quantity ?? null,
                }
              : {}),
          },
        },
        // IMPORTANTE: Disabilita qualsiasi refetch automatico
        refetchQueries: [],
        awaitRefetchQueries: false,
        // Aggiorna la cache invece di fare refetch completo (più veloce)
        // NOTA: La mutation salva comunque nel DB, questa è solo per aggiornare l'UI immediatamente
        update: (cache, { data: mutationData }) => {
          try {
            // Evita refetch se la mutation non è riuscita
            if (!mutationData) return;

            const queryVars = {
              dateRange: { from: dateRange.from, to: dateRange.to },
              ...(countryFilter?.length ? { countries: countryFilter } : {}),
              ...(customerTypeFilter
                ? { customerType: customerTypeFilter }
                : {}),
            };
            const existingData = cache.readQuery<{
              getInventoryData: InventoryProduct[];
            }>({
              query: GET_INVENTORY_DATA,
              variables: queryVars,
            });

            if (existingData) {
              const updatedData = existingData.getInventoryData.map(
                (product) =>
                  product.productId === productId
                    ? {
                        ...product,
                        todoStatus: status === null ? null : status,
                        isSkipped: false,
                        notes:
                          status === 'restocked'
                            ? 'Marked as restocked'
                            : status === null
                              ? null
                              : product.notes || null,
                        ...(status === 'restocked'
                          ? {
                              restockedDate: restocked?.date ?? null,
                              restockedQuantity: restocked?.quantity ?? null,
                            }
                          : {}),
                      }
                    : product,
              );

              cache.writeQuery({
                query: GET_INVENTORY_DATA,
                variables: queryVars,
                data: { getInventoryData: updatedData },
              });
            }
          } catch (err) {
            // Non facciamo refetch automatico, il DB è già salvato
            console.error('[INVENTORY] Cache update error:', err);
          }
        },
      });
    } catch (error) {
      console.error('Error updating inventory status:', error);
      alert('Error updating status.');
      // In caso di errore, NON fare refetch - l'utente può riprovare
      // refetch(); // Rimosso per mantenere performance
    }
  };

  const openRestockDialogFor = (product: InventoryProduct) => {
    setRestockTarget(product);
    setRestockQuantity('');
    setRestockDate(new Date().toISOString().slice(0, 10));
    setRestockDialogOpen(true);
  };

  const confirmRestock = async () => {
    if (!restockTarget) return;
    const qRaw = restockQuantity.trim();
    const q = qRaw ? Number(qRaw) : null;
    await handleUpdateStatus(restockTarget.productId, 'restocked', {
      date: restockDate,
      quantity:
        q != null && Number.isFinite(q) ? Math.max(0, Math.floor(q)) : null,
    });
    setRestockDialogOpen(false);
    setRestockTarget(null);
  };

  const handleSkipProduct = async (productId: string) => {
    try {
      await skipProduct({
        variables: { productId },
        // IMPORTANTE: Disabilita qualsiasi refetch automatico
        refetchQueries: [],
        awaitRefetchQueries: false,
        // Aggiorna la cache invece di fare refetch completo (più veloce)
        // NOTA: La mutation salva comunque nel DB, questa è solo per aggiornare l'UI immediatamente
        update: (cache, { data: mutationData }) => {
          try {
            // Evita refetch se la mutation non è riuscita
            if (!mutationData) return;

            const queryVars = {
              dateRange: { from: dateRange.from, to: dateRange.to },
              ...(countryFilter?.length ? { countries: countryFilter } : {}),
              ...(customerTypeFilter
                ? { customerType: customerTypeFilter }
                : {}),
            };
            const existingData = cache.readQuery<{
              getInventoryData: InventoryProduct[];
            }>({
              query: GET_INVENTORY_DATA,
              variables: queryVars,
            });

            if (existingData) {
              const updatedData = existingData.getInventoryData.map(
                (product) =>
                  product.productId === productId
                    ? {
                        ...product,
                        isSkipped: true,
                        todoStatus: null, // null per pending (Apollo non accetta undefined)
                        notes: product.notes || null,
                      }
                    : product,
              );

              cache.writeQuery({
                query: GET_INVENTORY_DATA,
                variables: queryVars,
                data: { getInventoryData: updatedData },
              });
            }
          } catch (err) {
            // Non facciamo refetch automatico, il DB è già salvato
            console.error('[INVENTORY] Cache update error:', err);
          }
        },
      });
    } catch (error) {
      console.error('Error skipping product:', error);
      alert('Error skipping product.');
      // In caso di errore, NON fare refetch - l'utente può riprovare
      // refetch(); // Rimosso per mantenere performance
    }
  };

  const handleUpdateThreshold = async () => {
    try {
      await updateInventoryThreshold({
        variables: { threshold },
      });
      setShowThresholdDialog(false);
      // Non fare refetch - la soglia viene applicata lato client
      // refetch(); // Rimosso per mantenere performance
    } catch (error) {
      console.error('Error updating threshold:', error);
    }
  };

  const [comparison, setComparison] = useState<{
    curr: number;
    prev: number;
    delta: number;
    isIncrease: boolean;
  } | null>(null);

  const handleGenerateAIReport = async () => {
    setIsGeneratingReport(true);
    try {
      // Chiamata JSON preflight per ottenere anche comparison e HTML deterministico
      const preflight = await generateAIReportData({
        variables: {
          dateRange: {
            from: dateRange.from,
            to: dateRange.to,
          },
        },
      });
      const payload = preflight.data?.generateInventoryAIReportData;
      // Supporta sia il vecchio shape (object) che il nuovo (string HTML diretto)
      if (typeof payload === 'string') {
        setAiReport(payload);
        setComparison(null);
      } else if (payload && payload.html) {
        setAiReport(payload.html);
        setComparison(payload.comparison || null);
      } else {
        throw new Error('Preflight payload non valido');
      }
    } catch (error) {
      console.error('Error generating AI report (preflight):', error);
      // Fallback alla vecchia mutation per garantire continuità
      try {
        const result = await generateAIReport({
          variables: {
            dateRange: {
              from: dateRange.from,
              to: dateRange.to,
            },
          },
        });
        setAiReport(result.data?.generateInventoryAIReport || null);
        setComparison(null);
      } catch (err2) {
        console.error('Error generating AI report (fallback):', err2);
        alert('Error generating the AI report. Please try again later.');
      }
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Funzione per aspettare che le immagini si carichino
  const waitForImages = (element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
      const images = element.querySelectorAll('img');
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          checkComplete();
        } else {
          img.onload = checkComplete;
          img.onerror = checkComplete;
        }
      });
    });
  };

  // Funzione per download PDF
  const downloadAsPDF = useCallback(async () => {
    try {
      // Carica html2pdf.js da CDN
      if (!(window as any).html2pdf) {
        const script = document.createElement('script');
        script.src =
          'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = () => {
          // Ricarica la funzione dopo che lo script è caricato
          setTimeout(() => downloadAsPDF(), 100);
        };
        document.head.appendChild(script);
        return;
      }

      const element = document.querySelector('.kipify-report') as HTMLElement;
      if (!element) {
        console.error('Report element not found');
        return;
      }

      // Nascondi controlli non utili nel PDF (es. pulsanti header)
      const headerActions = document.querySelector(
        '.header-actions',
      ) as HTMLElement | null;
      const originalHeaderDisplay = headerActions?.style.display;
      if (headerActions) headerActions.style.display = 'none';

      // Aspetta che le immagini si carichino
      await waitForImages(element);

      const opt = {
        margin: 0.5,
        filename: 'kipify-inventory-report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          scrollX: 0,
          scrollY: 0,
        },
        // Evita tagli a metà sezione
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      };

      await (window as any).html2pdf().set(opt).from(element).save();

      // Ripristina header
      if (headerActions && originalHeaderDisplay !== undefined) {
        headerActions.style.display = originalHeaderDisplay;
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  }, []);

  // Event listener per il pulsante PDF
  useEffect(() => {
    if (aiReport) {
      const handleDownloadClick = (event: Event) => {
        if ((event.target as HTMLElement).id === 'download-pdf-btn') {
          event.preventDefault();
          downloadAsPDF();
        }
      };

      // Aggiungi event listener quando il modal è aperto
      document.addEventListener('click', handleDownloadClick);

      // Cleanup quando il modal si chiude
      return () => {
        document.removeEventListener('click', handleDownloadClick);
      };
    }
  }, [aiReport, downloadAsPDF]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      // PRIORITÀ: Skipped sempre alla fine, indipendentemente dall'ordinamento
      const aIsSkipped = a.isSkipped ?? false;
      const bIsSkipped = b.isSkipped ?? false;

      if (aIsSkipped && !bIsSkipped) return 1; // a va alla fine
      if (!aIsSkipped && bIsSkipped) return -1; // b va alla fine
      if (aIsSkipped && bIsSkipped) {
        // Se entrambi sono skipped, ordina normalmente
      }

      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Logica speciale per ordinamento Status
      if (sortField === 'todoStatus') {
        // Ordina per: restocked (1) > to_restock (2) > pending (3)
        const getStatusValue = (product: any) => {
          if (product.todoStatus === 'restocked') return 1;
          if (product.todoStatus === 'to_restock') return 2;
          if (product.isLowStock) return 2; // To Restock
          return 3; // Pending
        };

        aValue = getStatusValue(a);
        bValue = getStatusValue(b);
      }

      if (sortField === 'daysToSoldOut') {
        aValue = aValue ?? Infinity;
        bValue = bValue ?? Infinity;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [filteredData, sortField, sortOrder]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // Export CSV: esporta tutti i dati filtrati/ordinati (non solo la pagina corrente)
  const fmtEur = (v: number | undefined | null) =>
    typeof v === 'number' && Number.isFinite(v)
      ? new Intl.NumberFormat('it-IT', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(v)
      : '';

  const downloadCSV = useCallback(() => {
    if (!sortedData.length) return;

    const periodLabel = `${dateRange.from} → ${dateRange.to}`;

    const rows = sortedData.map((p, i) => ({
      '#': i + 1,
      Period: periodLabel,
      Product: p.productTitle,
      Image: p.imageUrl || '',
      Type: p.productType || '',
      Vendor: p.vendor || '',
      'Sales/day': Number(p.dailySalesRate?.toFixed(2) ?? 0),
      Sold: p.totalSold,
      'Revenue (EUR)': fmtEur(p.totalRevenue),
      Stock: p.currentStock,
      'Days to sold out':
        p.daysToSoldOut != null ? Math.round(p.daysToSoldOut) : 'N/A',
      Score:
        typeof p.score === 'number' && Number.isFinite(p.score)
          ? new Intl.NumberFormat('it-IT', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(p.score)
          : p.score ?? '',
      Status: p.todoStatus || (p.isSkipped ? 'Skipped' : 'Pending'),
      Notes: p.notes || '',
      Tags: p.tags?.join(', ') || '',
      Collections: p.collections?.join(', ') || '',
    }));

    const csv = unparse(rows);
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `inventory_${dateRange.from}_${dateRange.to}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [sortedData, dateRange]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const toggleProductExpansion = (productId: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  const handleEditProduct = (product: InventoryProduct) => {
    setEditingProduct(product.productId);
    setEditNotes(product.notes || '');
    setEditStatus(product.todoStatus || 'to_restock');
  };

  const handleSaveEdit = async () => {
    if (!editingProduct) return;

    try {
      await updateInventoryStatus({
        variables: {
          input: {
            productId: editingProduct,
            todoStatus: editStatus,
            notes: editNotes,
          },
        },
        // IMPORTANTE: Disabilita qualsiasi refetch automatico
        refetchQueries: [],
        awaitRefetchQueries: false,
        // Aggiorna la cache invece di fare refetch completo (più veloce)
        update: (cache) => {
          try {
            const queryVars = {
              dateRange: { from: dateRange.from, to: dateRange.to },
              ...(countryFilter?.length ? { countries: countryFilter } : {}),
              ...(customerTypeFilter
                ? { customerType: customerTypeFilter }
                : {}),
            };
            const existingData = cache.readQuery<{
              getInventoryData: InventoryProduct[];
            }>({
              query: GET_INVENTORY_DATA,
              variables: queryVars,
            });

            if (existingData) {
              const updatedData = existingData.getInventoryData.map(
                (product) =>
                  product.productId === editingProduct
                    ? {
                        ...product,
                        todoStatus: editStatus || null,
                        notes: editNotes || null,
                        isSkipped: false,
                      }
                    : product,
              );

              cache.writeQuery({
                query: GET_INVENTORY_DATA,
                variables: queryVars,
                data: { getInventoryData: updatedData },
              });
            }
          } catch (err) {
            // Non facciamo refetch automatico, il DB è già salvato
          }
        },
      });

      alert('Status updated successfully.');
      setEditingProduct(null);
      // Non fare refetch - la cache è già aggiornata
      // refetch(); // Rimosso per mantenere performance
    } catch (error) {
      alert('Error updating status.');
    }
  };

  const getStatusChip = (
    isLowStock: boolean,
    status?: string,
    isSkipped?: boolean,
  ) => {
    if (isSkipped) {
      return <Chip label="Skipped" color="default" size="small" />;
    }
    if (status === 'restocked') {
      return <Chip label="Restocked" color="success" size="small" />;
    }

    // Mostra "To Restock" se isLowStock è true O se todoStatus è 'to_restock'
    // Questo allinea la visualizzazione con il filtro che include entrambi i casi
    if (isLowStock || status === 'to_restock') {
      return <Chip label="To Restock" color="warning" size="small" />;
    }

    return <Chip label="Pending" color="default" size="small" />;
  };

  const getDaysToSoldOutColor = (days?: number) => {
    if (!days) return palette.text.secondary;
    if (days <= 7) return palette.error.main;
    if (days <= 30) return palette.warning.main;
    return palette.success.main;
  };

  // Allow trial users to access inventory management
  // Only block if user has no plan at all
  if (!currentPlan) {
    return (
      <InventoryContainer sx={containerSxWhenCollapsed}>
        <ContentContainer>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ color: palette.primary.main, fontWeight: 700 }}
          >
            Inventory Management
          </Typography>

          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Plan Required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please select a plan to access inventory management.
              </Typography>
              <Button
                variant="contained"
                href="/billing"
                sx={{ bgcolor: palette.primary.main }}
              >
                Select Plan
              </Button>
            </CardContent>
          </Card>
        </ContentContainer>
      </InventoryContainer>
    );
  }

  if (loading || compareLoading) {
    return (
      <InventoryContainer sx={containerSxWhenCollapsed}>
        <ContentContainer>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Preparing your data...
              </Typography>
            </Box>
          </Box>
        </ContentContainer>
      </InventoryContainer>
    );
  }

  if (error || compareError) {
    return (
      <InventoryContainer sx={containerSxWhenCollapsed}>
        <ContentContainer>
          <Alert severity="error">
            Error loading inventory data:{' '}
            {error?.message || compareError?.message}
          </Alert>
        </ContentContainer>
      </InventoryContainer>
    );
  }

  const controlHeight = 40;
  const controlRadius = 2;
  const controlSx = {
    height: controlHeight,
    minHeight: controlHeight,
    borderRadius: controlRadius,
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      height: controlHeight,
      minHeight: controlHeight,
      borderRadius: controlRadius,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.primary.main,
    },
  };
  const labelSx = {
    display: 'block',
    mb: 0.5,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: palette.grey[500],
  };

  return (
    <InventoryContainer sx={containerSxWhenCollapsed}>
      <ContentContainer>
        <Box
          sx={{
            height: 4,
            borderRadius: '0 0 4px 4px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
            mb: 2,
            mx: -0.5,
          }}
        />

        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
            background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
            mt: -0.5,
            pt: 2,
            px: 2,
            mx: -2,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${palette.primary.main}18, transparent)`,
              opacity: 0.6,
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: `${palette.primary.main}18`,
              border: '1px solid',
              borderColor: `${palette.primary.main}35`,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: palette.primary.main,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${palette.primary.main}40`,
              }}
            />
            <Typography
              sx={{
                color: palette.primary.main,
                fontWeight: 700,
                letterSpacing: 1.1,
                fontSize: '0.7rem',
              }}
            >
              INVENTORY
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Typography
              variant="h4"
              sx={{
                color: palette.grey[800],
                fontWeight: 800,
                letterSpacing: '-0.025em',
                lineHeight: 1.2,
              }}
            >
              Stock velocity &amp; coverage
            </Typography>
            <Box
              sx={{
                width: '60%',
                height: 3,
                borderRadius: '0 0 2px 2px',
                background: `linear-gradient(90deg, ${palette.primary.main}, transparent)`,
                opacity: 0.5,
                mt: 0.5,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 1.5, maxWidth: 520 }}
          >
            Spot sell-through, runway and restock risk in one place—same flow as
            Products &amp; Category analysis.
          </Typography>
          {inventoryKeyInsight && (
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, ${palette.primary.main}08 100%)`,
                border: '1px solid',
                borderColor: `${palette.primary.main}25`,
                borderLeft: '5px solid',
                borderLeftColor: palette.primary.main,
                boxShadow: `0 4px 20px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)`,
                animation: `${insightGlowKeyframes} 1.2s ease-out 1`,
                position: 'relative',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: palette.grey[600],
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Key insight
              </Typography>
              {inventoryKeyInsight.totalSold > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    maxWidth: 480,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '1.75rem',
                        fontWeight: 800,
                        color: palette.primary.main,
                        textShadow: `0 0 24px ${palette.primary.main}35`,
                        letterSpacing: '-0.03em',
                        lineHeight: 1,
                      }}
                    >
                      {inventoryKeyInsight.unitsPct}%
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: palette.grey[600] }}
                    >
                      of units sold come from the top{' '}
                      <strong>{inventoryKeyInsight.topPct}%</strong> of SKUs
                      sold
                      {' · '}
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.75rem',
                          color: palette.grey[800],
                          fontWeight: 600,
                        }}
                      >
                        {inventoryKeyInsight.topK} of{' '}
                        {inventoryKeyInsight.totalProducts} SKUs
                      </Box>
                    </Typography>
                  </Box>
                  {/* Stesso contrasto di Product Analysis: grey[800] + peso 600, non text.secondary */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: palette.grey[800],
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      lineHeight: 1.45,
                      pl: { xs: 0, sm: 0.25 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: palette.primary.main,
                        fontWeight: 800,
                        fontSize: '1rem',
                        mr: 0.5,
                      }}
                    >
                      {inventoryKeyInsight.lowCount}
                    </Box>
                    {inventoryKeyInsight.lowCount === 1
                      ? 'SKU is below your alert threshold — prioritize restock.'
                      : 'SKUs are below your alert threshold — prioritize restock.'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No unit sales in this period—try a wider date range or verify
                  order sync.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            mb: 1.5,
            flexWrap: 'wrap',
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid',
            borderColor: palette.grey[200],
          }}
        >
          <Box>
            <Typography component="label" sx={labelSx}>
              Period
            </Typography>
            <FormControl size="small" sx={{ width: 145, ...controlSx }}>
              <Select
                value={dateRangePreset}
                onChange={(e) => {
                  const preset = e.target.value;
                  if (preset !== 'custom' && preset !== 'this_month') {
                    const days = parseInt(preset, 10);
                    if (!isNaN(days) && !canUsePresetDays(days)) {
                      snackbar(
                        getLookbackPresetBlockedMessage(lookbackDays),
                        'warning',
                      );
                      return;
                    }
                  }
                  if (preset === 'this_month' && !canUseThisMonthPreset) {
                    snackbar(
                      getLookbackThisMonthBlockedMessage(lookbackDays),
                      'warning',
                    );
                    return;
                  }
                  setDateRangePreset(preset);
                  setShowCustomDateRange(preset === 'custom');
                  setTempCustomDates(null);
                  if (preset === 'this_month') {
                    const newDateRange = getThisMonthDateRange();
                    setDateRange(newDateRange);
                    if (compareMode === 'previous_period')
                      setCompareDateRange(
                        getPreviousPeriodDateRange(newDateRange),
                      );
                    else if (compareMode === 'previous_year')
                      setCompareDateRange(
                        getPreviousYearDateRange(newDateRange),
                      );
                  } else if (preset !== 'custom') {
                    const days = parseInt(preset, 10);
                    const newDateRange = getLastNDaysDateRange(days);
                    setDateRange(newDateRange);
                    if (compareMode === 'previous_period')
                      setCompareDateRange(
                        getPreviousPeriodDateRange(newDateRange),
                      );
                    else if (compareMode === 'previous_year')
                      setCompareDateRange(
                        getPreviousYearDateRange(newDateRange),
                      );
                  } else {
                    setTempCustomDates({
                      from: dateRange.from,
                      to: dateRange.to,
                    });
                  }
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0,
                    height: controlHeight - 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="7" disabled={!canUsePresetDays(7)}>
                  Last 7 days
                </MenuItem>
                <MenuItem value="30" disabled={!canUsePresetDays(30)}>
                  Last 30 days
                </MenuItem>
                <MenuItem value="90" disabled={!canUsePresetDays(90)}>
                  Last 90 days
                </MenuItem>
                <MenuItem value="this_month" disabled={!canUseThisMonthPreset}>
                  This month
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {showCustomDateRange && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={tempCustomDates?.from ?? dateRange.from}
                onChange={(e) => {
                  const v = e.target.value;
                  const t = tempCustomDates?.to ?? dateRange.to;
                  setTempCustomDates(
                    t && v > t
                      ? { from: v, to: v }
                      : { from: v, to: tempCustomDates?.to ?? dateRange.to },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: lookbackMinDate || undefined,
                  max: (tempCustomDates?.to ?? dateRange.to) || undefined,
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={tempCustomDates?.to ?? dateRange.to}
                onChange={(e) => {
                  const v = e.target.value;
                  const f = tempCustomDates?.from ?? dateRange.from;
                  setTempCustomDates(
                    f && v < f
                      ? { from: v, to: v }
                      : {
                          from: tempCustomDates?.from ?? dateRange.from,
                          to: v,
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: (tempCustomDates?.from ?? dateRange.from) || undefined,
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <Button
                variant="contained"
                size="small"
                className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                onClick={() => {
                  if (
                    tempCustomDates?.from &&
                    tempCustomDates?.to &&
                    tempCustomDates.from <= tempCustomDates.to
                  ) {
                    if (!isRangeWithinLookback(tempCustomDates)) {
                      snackbar(
                        getLookbackCustomRangeBlockedMessage(
                          lookbackDays,
                          'date',
                        ),
                        'warning',
                      );
                      return;
                    }
                    setDateRange(tempCustomDates);
                    setTempCustomDates(null);
                  }
                }}
                disabled={
                  !tempCustomDates?.from ||
                  !tempCustomDates?.to ||
                  tempCustomDates.from > tempCustomDates.to
                }
                sx={{
                  minHeight: controlHeight,
                  borderRadius: controlRadius,
                  bgcolor: palette.primary.main,
                  color: palette.primary.contrastText,
                  '&:hover': { bgcolor: palette.primary.light, opacity: 0.9 },
                  minWidth: 60,
                }}
              >
                Apply
              </Button>
            </>
          )}

          <Box>
            <Typography component="label" sx={labelSx}>
              Compare
            </Typography>
            <FormControl size="small" sx={{ width: 155, ...controlSx }}>
              <Select
                value={compareMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  if (
                    mode === 'previous_period' &&
                    !canUsePreviousPeriodCompare
                  ) {
                    snackbar(
                      getLookbackCompareBlockedMessage(
                        'previous_period',
                        lookbackDays,
                      ),
                      'warning',
                    );
                    return;
                  }
                  if (mode === 'previous_year' && !canUsePreviousYearCompare) {
                    snackbar(
                      getLookbackCompareBlockedMessage(
                        'previous_year',
                        lookbackDays,
                      ),
                      'warning',
                    );
                    return;
                  }
                  setCompareMode(mode);
                  if (mode === 'none') {
                    setCompareDateRange(null);
                    setShowCustomCompareDates(false);
                    setTempCustomCompareDates(null);
                  } else if (mode === 'previous_period') {
                    setCompareDateRange(getPreviousPeriodDateRange(dateRange));
                    setShowCustomCompareDates(false);
                  } else if (mode === 'previous_year') {
                    setCompareDateRange(getPreviousYearDateRange(dateRange));
                    setShowCustomCompareDates(false);
                  } else if (mode === 'custom') {
                    setShowCustomCompareDates(true);
                    setTempCustomCompareDates(
                      compareDateRange || {
                        from: dateRange.from,
                        to: dateRange.to,
                      },
                    );
                  }
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0,
                    height: controlHeight - 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem
                  value="previous_period"
                  disabled={!canUsePreviousPeriodCompare}
                >
                  Previous period
                </MenuItem>
                <MenuItem
                  value="previous_year"
                  disabled={!canUsePreviousYearCompare}
                >
                  Previous year
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {showCustomCompareDates && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={
                  tempCustomCompareDates?.from ?? compareDateRange?.from ?? ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  const t = tempCustomCompareDates?.to ?? compareDateRange?.to;
                  setTempCustomCompareDates(
                    t && v > t
                      ? { from: v, to: v }
                      : {
                          from: v,
                          to:
                            tempCustomCompareDates?.to ??
                            compareDateRange?.to ??
                            '',
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: lookbackMinDate || undefined,
                  max:
                    (tempCustomCompareDates?.to ?? compareDateRange?.to) ||
                    undefined,
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={tempCustomCompareDates?.to ?? compareDateRange?.to ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const f =
                    tempCustomCompareDates?.from ?? compareDateRange?.from;
                  setTempCustomCompareDates(
                    f && v < f
                      ? { from: f, to: f }
                      : {
                          from:
                            tempCustomCompareDates?.from ??
                            compareDateRange?.from ??
                            '',
                          to: v,
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min:
                    (tempCustomCompareDates?.from ?? compareDateRange?.from) ||
                    undefined,
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <Button
                variant="contained"
                size="small"
                className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                onClick={() => {
                  if (
                    tempCustomCompareDates?.from &&
                    tempCustomCompareDates?.to &&
                    tempCustomCompareDates.from <= tempCustomCompareDates.to
                  ) {
                    if (!isRangeWithinLookback(tempCustomCompareDates)) {
                      snackbar(
                        getLookbackCustomRangeBlockedMessage(
                          lookbackDays,
                          'compare',
                        ),
                        'warning',
                      );
                      return;
                    }
                    setCompareDateRange(tempCustomCompareDates);
                    setShowCustomCompareDates(false);
                    setTempCustomCompareDates(null);
                  }
                }}
                disabled={
                  !tempCustomCompareDates?.from ||
                  !tempCustomCompareDates?.to ||
                  tempCustomCompareDates.from > tempCustomCompareDates.to
                }
                sx={{
                  minHeight: controlHeight,
                  borderRadius: controlRadius,
                  bgcolor: palette.primary.main,
                  color: palette.primary.contrastText,
                  '&:hover': { bgcolor: palette.primary.light, opacity: 0.9 },
                  minWidth: 60,
                }}
              >
                Apply
              </Button>
            </>
          )}
          {lookbackDays !== null && lookbackDays !== undefined && (
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                color: 'warning.main',
                fontWeight: 500,
                mt: 0.5,
              }}
            >
              {getLookbackPlanLimitCaption(lookbackDays)}
            </Typography>
          )}

          <Box
            sx={{
              flex: '1 1 260px',
              minWidth: { xs: '100%', sm: 220 },
            }}
          >
            <Typography component="label" sx={labelSx}>
              Search
            </Typography>
            <TextField
              placeholder="Products, variants, or SKU…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              sx={{
                ...controlSx,
                '& .MuiOutlinedInput-root': {
                  ...controlSx['& .MuiOutlinedInput-root'],
                  py: 0,
                },
                '& .MuiInputBase-input': {
                  py: 1,
                  fontSize: '0.875rem',
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 0.75, color: 'text.secondary' }}>🔍</Box>
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{ mr: -0.5 }}
                  >
                    ✕
                  </IconButton>
                ),
              }}
            />
          </Box>

          <Box>
            <Typography component="label" sx={labelSx}>
              Filters
            </Typography>
            <Button
              size="small"
              variant={showMoreFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    transform: showMoreFilters ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              }
              onClick={() => setShowMoreFilters((v) => !v)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 1.5,
                minHeight: controlHeight,
                borderRadius: controlRadius,
                whiteSpace: 'nowrap',
                fontSize: '0.8125rem',
                borderColor:
                  activeFilterCount > 0 ? palette.primary.main : '#e0e0e0',
                color: showMoreFilters ? '#fff' : 'text.secondary',
                bgcolor: showMoreFilters ? palette.primary.main : 'white',
                '&:hover': {
                  bgcolor: showMoreFilters
                    ? palette.primary.light
                    : 'rgba(0,0,0,0.04)',
                  borderColor: palette.primary.main,
                },
              }}
            >
              Filters
              {activeFilterCount > 0 && (
                <Chip
                  label={activeFilterCount}
                  size="small"
                  sx={{
                    ml: 0.75,
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: showMoreFilters
                      ? 'rgba(255,255,255,0.3)'
                      : palette.primary.main,
                    color: '#fff',
                  }}
                />
              )}
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 1.5 },
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              ml: { xs: 0, md: 'auto' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            {/* Threshold Button */}
            <Button
              variant="contained"
              onClick={() => setShowThresholdDialog(true)}
              sx={{
                backgroundColor: palette.primary.main,
                color: 'black',
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: 1.2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                minWidth: { xs: '140px', sm: '180px' },
                boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                border: '2px solid transparent',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: palette.primary.main,
                  opacity: 0.95,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                  border: '2px solid #059669',
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>⚙️ </Box>
              Alert: {threshold}d
            </Button>

            {/* AI Report Button with Info - Hidden on mobile due to responsive issues */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      aiCallsRemaining >= 5
                        ? '#6366f1'
                        : aiCallsRemaining > 0
                          ? '#f59e0b'
                          : '#ef4444',
                    fontSize: '12px',
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  {aiCallsRemaining >= 5
                    ? '5 AI credits'
                    : `${aiCallsRemaining}/5 AI credits`}
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1, color: '#000' }}
                      >
                        AI Report Cost: 5 AI Credits
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 0.5, color: '#000' }}
                      >
                        • AI credits are used to generate intelligent inventory
                        insights
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 0.5, color: '#000' }}
                      >
                        • Your remaining credits:{' '}
                        {aiCallsRemaining || 'Loading...'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#000' }}
                      >
                        • Upgrade your plan for more AI interactions
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor:
                        aiCallsRemaining >= 5
                          ? '#6366f1'
                          : aiCallsRemaining > 0
                            ? '#f59e0b'
                            : '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'help',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        lineHeight: 1,
                      }}
                    >
                      i
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
              <Button
                variant="contained"
                onClick={handleGenerateAIReport}
                disabled={isGeneratingReport || aiCallsRemaining < 5}
                sx={{
                  backgroundColor: '#6366f1',
                  color: 'white',
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  minWidth: '140px',
                  boxShadow: '0 3px 12px rgba(99, 102, 241, 0.3)',
                  border: '2px solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#4f46e5',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                    border: '2px solid #3730a3',
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                    color: '#6b7280',
                    boxShadow: 'none',
                  },
                }}
              >
                {isGeneratingReport ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: 'white' }}
                    />
                    Generating...
                  </>
                ) : (
                  'AI Report'
                )}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Active filter chips (senza duplicare nei campi) */}
        {activeFilterCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {countryFilter.map((c) => (
              <Chip
                key={`cf-${c}`}
                label={c}
                size="small"
                onDelete={() =>
                  setCountryFilter((p) => p.filter((x) => x !== c))
                }
                sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
              />
            ))}
            {customerTypeFilter && (
              <Chip
                label={
                  customerTypeFilter === 'First time' ? 'New' : 'Recurring'
                }
                size="small"
                onDelete={() => setCustomerTypeFilter('')}
                sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
              />
            )}
            {tagFilter.map((t) => (
              <Chip
                key={`tf-${t}`}
                label={t}
                size="small"
                onDelete={() => setTagFilter((p) => p.filter((x) => x !== t))}
                sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
              />
            ))}
            {collectionFilter.map((c) => (
              <Chip
                key={`col-${c}`}
                label={c}
                size="small"
                onDelete={() =>
                  setCollectionFilter((p) => p.filter((x) => x !== c))
                }
                sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
              />
            ))}
            {vendorFilter.map((v) => (
              <Chip
                key={`vf-${v}`}
                label={v}
                size="small"
                onDelete={() =>
                  setVendorFilter((p) => p.filter((x) => x !== v))
                }
                sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
              />
            ))}
            {productTypeFilter.map((p) => (
              <Chip
                key={`pt-${p}`}
                label={p}
                size="small"
                onDelete={() =>
                  setProductTypeFilter((prev) => prev.filter((x) => x !== p))
                }
                sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setCountryFilter([]);
                setCustomerTypeFilter('');
                setTagFilter([]);
                setCollectionFilter([]);
                setVendorFilter([]);
                setProductTypeFilter([]);
              }}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                color: 'text.secondary',
                minWidth: 0,
                px: 0.75,
              }}
            >
              Clear
            </Button>
          </Box>
        )}

        {/* ── Expandable filters panel (tutti i filtri raggruppati) ── */}
        <Collapse in={showMoreFilters} timeout={200}>
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
              mb: 1.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#fafafa',
              border: '1px solid #f0f0f0',
            }}
          >
            {/* Country */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Country
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableCountries}
                value={countryFilter}
                onChange={(_, v) => setCountryFilter(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{option}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select countries..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>

            {/* Customer Type */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Customer type
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={customerTypeFilter}
                onChange={(e) => setCustomerTypeFilter(e.target.value)}
                sx={{ backgroundColor: 'white' }}
                renderValue={(v) =>
                  v ? (
                    v === 'First time' ? (
                      'New (first time)'
                    ) : (
                      'Recurring'
                    )
                  ) : (
                    <Typography color="text.disabled">All customers</Typography>
                  )
                }
              >
                <MenuItem value="">All customers</MenuItem>
                <MenuItem value="First time">New (first time)</MenuItem>
                <MenuItem value="recurring">Recurring</MenuItem>
              </Select>
            </Box>

            {/* Tag */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Tag
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableTags}
                value={tagFilter}
                onChange={(_, v) => setTagFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select tags..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>

            {/* Collection */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Collection
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableCollections}
                value={collectionFilter}
                onChange={(_, v) => setCollectionFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select collections..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>

            {/* Vendor */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Vendor
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableVendors}
                value={vendorFilter}
                onChange={(_, v) => setVendorFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select vendors..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>

            {/* Product Type */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Product Type
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableProductTypes}
                value={productTypeFilter}
                onChange={(_, v) => setProductTypeFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select types..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
          </Box>
        </Collapse>

        {/* Indicatore risultati ricerca */}
        {searchTerm && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredData.length} of {inventoryData.length} products found
              {searchTerm && ` for "${searchTerm}"`}
            </Typography>
          </Box>
        )}

        {/* Inventory table / empty state */}
        <>
          {sortedData.length === 0 ? (
            <Card sx={{ mt: 1 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No inventory data
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {searchTerm
                    ? 'No products match your search and filters.'
                    : 'No products have sales in the selected period. Try expanding the date range (e.g. Last 90 days) or choose a custom range.'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {searchTerm ? (
                    <Button
                      variant="outlined"
                      onClick={() => setSearchTerm('')}
                      sx={{
                        borderColor: palette.primary.main,
                        color: palette.primary.main,
                      }}
                    >
                      Clear search
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => {
                        setDateRangePreset('90');
                        setShowCustomDateRange(false);
                        setTempCustomDates(null);
                        const newDateRange = getLastNDaysDateRange(90);
                        setDateRange(newDateRange);
                        if (compareMode === 'previous_period') {
                          setCompareDateRange(
                            getPreviousPeriodDateRange(newDateRange),
                          );
                        } else if (compareMode === 'previous_year') {
                          setCompareDateRange(
                            getPreviousYearDateRange(newDateRange),
                          );
                        }
                      }}
                      sx={{ bgcolor: palette.primary.main }}
                    >
                      Last 90 days
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    href="/connectors"
                    sx={{
                      borderColor: palette.primary.main,
                      color: palette.primary.main,
                    }}
                  >
                    Go to Connectors
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <TableContainer
                component={Paper}
                sx={{
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(30, 237, 159, 0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(30, 237, 159, 0.6)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(30, 237, 159, 0.8)',
                    },
                  },
                }}
              >
                <Table
                  sx={{
                    minWidth: { xs: 800, sm: 'auto' },
                    '& .MuiTableCell-root': { px: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: palette.background.tableRow }}>
                      <TableCell sx={{ minWidth: { xs: 200, sm: 'auto' } }}>
                        Product
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortField === 'dailySalesRate'}
                          direction={
                            sortField === 'dailySalesRate' ? sortOrder : 'asc'
                          }
                          onClick={() => handleSort('dailySalesRate')}
                        >
                          Sales/day
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: 'nowrap', minWidth: 88 }}
                      >
                        <TableSortLabel
                          active={sortField === 'totalSold'}
                          direction={
                            sortField === 'totalSold' ? sortOrder : 'asc'
                          }
                          onClick={() => handleSort('totalSold')}
                        >
                          Sold
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
                      >
                        <TableSortLabel
                          active={sortField === 'totalRevenue'}
                          direction={
                            sortField === 'totalRevenue' ? sortOrder : 'asc'
                          }
                          onClick={() => handleSort('totalRevenue')}
                        >
                          Revenue
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortField === 'currentStock'}
                          direction={
                            sortField === 'currentStock' ? sortOrder : 'asc'
                          }
                          onClick={() => handleSort('currentStock')}
                        >
                          Stock
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: 'nowrap',
                          minWidth: { xs: 100, sm: 'auto' },
                        }}
                      >
                        <TableSortLabel
                          active={sortField === 'daysToSoldOut'}
                          direction={
                            sortField === 'daysToSoldOut' ? sortOrder : 'asc'
                          }
                          onClick={() => handleSort('daysToSoldOut')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              fontSize: '0.875rem',
                            },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: { xs: 'none', sm: 'inline' },
                            }}
                          >
                            Days to sold out
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: { xs: 'inline', sm: 'none' },
                            }}
                          >
                            Days left
                          </Box>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <TableSortLabel
                          active={sortField === 'score'}
                          direction={sortField === 'score' ? sortOrder : 'asc'}
                          onClick={() => handleSort('score')}
                        >
                          Score
                        </TableSortLabel>
                      </TableCell>
                      {/* COMMENTATO: Colonna ScoreM (metrica marketing)
                  <TableCell
                    align="center"
                    sx={{ display: { xs: 'none', lg: 'table-cell' } }}
                  >
                    <TableSortLabel
                      active={sortField === 'scoreM'}
                      direction={sortField === 'scoreM' ? sortOrder : 'asc'}
                      onClick={() => handleSort('scoreM')}
                    >
                      ScoreM
                    </TableSortLabel>
                  </TableCell>
                  */}
                      <TableCell align="center">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1}
                        >
                          <TableSortLabel
                            active={sortField === 'todoStatus'}
                            direction={
                              sortField === 'todoStatus' ? sortOrder : 'asc'
                            }
                            onClick={() => handleSort('todoStatus')}
                          >
                            Status
                          </TableSortLabel>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterAnchorEl(e.currentTarget);
                            }}
                            sx={{
                              p: 1,
                              ml: 0.5,
                              minWidth: 28,
                              minHeight: 28,
                              border: `1px solid ${statusFilter !== 'all' ? '#1976d2' : '#ddd'}`,
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.08)',
                                borderColor: '#1976d2',
                              },
                            }}
                          >
                            <Tooltip title="Filter Status" placement="bottom">
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  aria-hidden="true"
                                >
                                  <rect
                                    x="2"
                                    y="4"
                                    width="12"
                                    height="2"
                                    rx="1"
                                    fill={
                                      statusFilter !== 'all'
                                        ? palette.primary.main
                                        : '#1e7e34'
                                    }
                                    transform="rotate(-12 2 4)"
                                  />
                                  <rect
                                    x="4"
                                    y="8"
                                    width="8"
                                    height="2"
                                    rx="1"
                                    fill={
                                      statusFilter !== 'all'
                                        ? palette.primary.main
                                        : '#1e7e34'
                                    }
                                    transform="rotate(-12 4 8)"
                                  />
                                  <rect
                                    x="6"
                                    y="12"
                                    width="4"
                                    height="2"
                                    rx="1"
                                    fill={
                                      statusFilter !== 'all'
                                        ? palette.primary.main
                                        : '#1e7e34'
                                    }
                                    transform="rotate(-12 6 12)"
                                  />
                                </svg>
                              </Box>
                            </Tooltip>
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading
                      ? // Skeleton loading per 10 righe
                        Array.from({ length: 10 }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{ minWidth: { xs: 200, sm: 'auto' } }}
                            >
                              <Box display="flex" alignItems="center" gap={2}>
                                <Skeleton
                                  variant="rectangular"
                                  width={24}
                                  height={24}
                                />
                                <Skeleton
                                  variant="rectangular"
                                  width={40}
                                  height={40}
                                />
                                <Skeleton variant="text" width={200} />
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={60} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={50} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={70} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={40} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={80} />
                            </TableCell>
                            <TableCell align="center">
                              <Skeleton
                                variant="circular"
                                width={24}
                                height={24}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Skeleton
                                variant="rectangular"
                                width={100}
                                height={24}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Skeleton
                                variant="rectangular"
                                width={120}
                                height={32}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      : paginatedData.map((product) => (
                          <React.Fragment key={product.productId}>
                            <TableRow hover>
                              <TableCell
                                sx={{ minWidth: { xs: 200, sm: 'auto' } }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={2}
                                  minWidth={0}
                                >
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      toggleProductExpansion(product.productId)
                                    }
                                  >
                                    {expandedProducts.has(product.productId)
                                      ? '▼'
                                      : '▶'}
                                  </Button>
                                  {product.imageUrl ? (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                        bgcolor: palette.grey[200],
                                        border: '1px solid #e0e0e0',
                                        position: 'relative',
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={product.imageUrl}
                                        alt={product.productTitle}
                                        onError={(e) => {
                                          const target =
                                            e.currentTarget as HTMLImageElement;
                                          target.style.display = 'none';
                                          const parent = target.parentElement;
                                          const fallback =
                                            parent?.querySelector(
                                              '[data-image-fallback]',
                                            ) as HTMLElement;
                                          if (fallback)
                                            fallback.style.display = 'flex';
                                        }}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'contain',
                                        }}
                                      />
                                      <Box
                                        data-image-fallback
                                        sx={{
                                          display: 'none',
                                          position: 'absolute',
                                          inset: 0,
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '12px',
                                          color: '#999',
                                          fontWeight: 500,
                                        }}
                                      >
                                        📦
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        bgcolor: palette.grey[200],
                                        border: '1px solid #e0e0e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        color: '#999',
                                        fontWeight: 500,
                                        flexShrink: 0,
                                      }}
                                    >
                                      📦
                                    </Box>
                                  )}
                                  <Tooltip
                                    title={product.productTitle}
                                    placement="top"
                                    enterDelay={300}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                      }}
                                    >
                                      {product.productTitle}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="flex-end"
                                  gap={0.5}
                                >
                                  <Typography variant="body2" fontWeight={500}>
                                    {product.dailySalesRate.toFixed(1)}
                                  </Typography>
                                  {product.changes?.dailySalesRate && (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {product.changes.dailySalesRate
                                        .isPositive ? (
                                        <TrendingUpIcon
                                          sx={{
                                            color: 'success.main',
                                            fontSize: 14,
                                          }}
                                        />
                                      ) : (
                                        <TrendingDownIcon
                                          sx={{
                                            color: 'warning.main',
                                            fontSize: 14,
                                          }}
                                        />
                                      )}
                                      <Typography
                                        variant="caption"
                                        color={
                                          product.changes.dailySalesRate
                                            .isPositive
                                            ? 'success.main'
                                            : 'warning.main'
                                        }
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {product.changes.dailySalesRate
                                          .isPositive
                                          ? '+'
                                          : ''}
                                        {
                                          product.changes.dailySalesRate
                                            .percentage
                                        }
                                        %
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.7rem', ml: 0.5 }}
                                      >
                                        was{' '}
                                        {product.changes.dailySalesRate.previousValue.toFixed(
                                          1,
                                        )}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={500}>
                                  {product.totalSold}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  {typeof product.totalRevenue === 'number' &&
                                  Number.isFinite(product.totalRevenue)
                                    ? new Intl.NumberFormat('it-IT', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                      }).format(product.totalRevenue)
                                    : '–'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={500}>
                                  {product.currentStock}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  minWidth: { xs: 100, sm: 'auto' },
                                }}
                              >
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="flex-end"
                                  gap={0.5}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                      color: getDaysToSoldOutColor(
                                        product.daysToSoldOut,
                                      ),
                                    }}
                                  >
                                    {product.daysToSoldOut != null
                                      ? `${Math.round(product.daysToSoldOut)} days`
                                      : 'N/A'}
                                  </Typography>
                                  {product.changes?.daysToSoldOut && (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {product.changes.daysToSoldOut
                                        .isPositive ? (
                                        <TrendingUpIcon
                                          sx={{
                                            color: 'success.main',
                                            fontSize: 14,
                                          }}
                                        />
                                      ) : (
                                        <TrendingDownIcon
                                          sx={{
                                            color: 'warning.main',
                                            fontSize: 14,
                                          }}
                                        />
                                      )}
                                      <Typography
                                        variant="caption"
                                        color={
                                          product.changes.daysToSoldOut
                                            .isPositive
                                            ? 'success.main'
                                            : 'warning.main'
                                        }
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {product.changes.daysToSoldOut
                                          .isPositive
                                          ? '+'
                                          : ''}
                                        {
                                          product.changes.daysToSoldOut
                                            .percentage
                                        }
                                        %
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.7rem', ml: 0.5 }}
                                      >
                                        was{' '}
                                        {product.changes.daysToSoldOut.previousValue.toFixed(
                                          0,
                                        )}{' '}
                                        days
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  {product.score ? (
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        backgroundColor:
                                          product.scoreColor || '#ccc',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        border: '2px solid #fff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        margin: '0 auto',
                                      }}
                                    >
                                      {product.score.toFixed(1)}
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      N/A
                                    </Typography>
                                  )}
                                  {product.changes?.score && (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {product.changes.score.isPositive ? (
                                        <TrendingUpIcon
                                          sx={{
                                            color: 'success.main',
                                            fontSize: 14,
                                          }}
                                        />
                                      ) : (
                                        <TrendingDownIcon
                                          sx={{
                                            color: 'warning.main',
                                            fontSize: 14,
                                          }}
                                        />
                                      )}
                                      <Typography
                                        variant="caption"
                                        color={
                                          product.changes.score.isPositive
                                            ? 'success.main'
                                            : 'warning.main'
                                        }
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {product.changes.score.isPositive
                                          ? '+'
                                          : ''}
                                        {product.changes.score.percentage}%
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.7rem', ml: 0.5 }}
                                      >
                                        was{' '}
                                        {product.changes.score.previousValue.toFixed(
                                          1,
                                        )}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              {/* COMMENTATO: Cella ScoreM (metrica marketing)
                          <TableCell
                            align="center"
                            sx={{ display: { xs: 'none', lg: 'table-cell' } }}
                          >
                            {product.scoreM ? (
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color="primary"
                              >
                                {(product.scoreM * 100).toFixed(1)}%
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          */}
                              <TableCell align="center">
                                {getStatusChip(
                                  product.isLowStock,
                                  product.todoStatus,
                                  product.isSkipped,
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <Select
                                    value={
                                      product.isSkipped
                                        ? 'skip'
                                        : product.todoStatus ||
                                          (product.isLowStock
                                            ? 'to_restock'
                                            : 'pending')
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === 'skip') {
                                        handleSkipProduct(product.productId);
                                      } else if (value === 'pending') {
                                        handleUpdateStatus(
                                          product.productId,
                                          null as any,
                                        );
                                      } else {
                                        if (value === 'restocked') {
                                          openRestockDialogFor(product);
                                        } else {
                                          handleUpdateStatus(
                                            product.productId,
                                            value as 'to_restock' | 'restocked',
                                          );
                                        }
                                      }
                                    }}
                                    displayEmpty
                                    sx={{
                                      '& .MuiSelect-select': {
                                        py: 0.5,
                                        px: 1,
                                      },
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline':
                                        {
                                          border: '1px solid #ccc',
                                        },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                        {
                                          border: '1px solid #1976d2',
                                        },
                                    }}
                                  >
                                    <MenuItem value="to_restock">
                                      <Chip
                                        label="To Restock"
                                        size="small"
                                        sx={{
                                          bgcolor: '#fff3cd',
                                          color: '#856404',
                                          fontWeight: 'bold',
                                          border: '1px solid #ffeaa7',
                                          '& .MuiChip-label': {
                                            px: 1,
                                          },
                                        }}
                                      />
                                    </MenuItem>
                                    <MenuItem value="restocked">
                                      <Chip
                                        label="Restocked"
                                        size="small"
                                        sx={{
                                          bgcolor: '#d4edda',
                                          color: '#155724',
                                          fontWeight: 'bold',
                                          border: '1px solid #c3e6cb',
                                          '& .MuiChip-label': {
                                            px: 1,
                                          },
                                        }}
                                      />
                                    </MenuItem>
                                    <MenuItem value="pending">
                                      <Chip
                                        label="Pending"
                                        size="small"
                                        sx={{
                                          bgcolor: '#e9ecef',
                                          color: '#495057',
                                          fontWeight: 'bold',
                                          border: '1px solid #dee2e6',
                                          '& .MuiChip-label': {
                                            px: 1,
                                          },
                                        }}
                                      />
                                    </MenuItem>
                                    <MenuItem value="skip">
                                      <Chip
                                        label="Skip"
                                        size="small"
                                        sx={{
                                          bgcolor: '#f8d7da',
                                          color: '#721c24',
                                          fontWeight: 'bold',
                                          border: '1px solid #f5c6cb',
                                          '& .MuiChip-label': {
                                            px: 1,
                                          },
                                        }}
                                      />
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>

                            {/* Dettaglio varianti */}
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 0 }}>
                                <Collapse
                                  in={expandedProducts.has(product.productId)}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box
                                    sx={{
                                      p: 2,
                                      bgcolor: palette.background.default,
                                    }}
                                  >
                                    <Typography variant="h6" gutterBottom>
                                      Variants
                                    </Typography>
                                    <Table
                                      size="small"
                                      sx={{ minWidth: { xs: 600, sm: 'auto' } }}
                                    >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell
                                            sx={{
                                              minWidth: { xs: 150, sm: 'auto' },
                                            }}
                                          >
                                            Variant
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              display: {
                                                xs: 'none',
                                                sm: 'table-cell',
                                              },
                                            }}
                                          >
                                            SKU
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              display: {
                                                xs: 'none',
                                                md: 'table-cell',
                                              },
                                            }}
                                          >
                                            Sales
                                          </TableCell>
                                          <TableCell align="right">
                                            Stock
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              display: {
                                                xs: 'none',
                                                sm: 'table-cell',
                                              },
                                            }}
                                          >
                                            Sales/day
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              display: {
                                                xs: 'none',
                                                lg: 'table-cell',
                                              },
                                            }}
                                          >
                                            Days to sold out
                                          </TableCell>
                                          <TableCell align="center">
                                            Status
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {[...product.variants]
                                          .sort(
                                            (a, b) =>
                                              b.dailySalesRate -
                                              a.dailySalesRate,
                                          )
                                          .map((variant) => (
                                            <TableRow key={variant.variantId}>
                                              <TableCell
                                                sx={{
                                                  minWidth: {
                                                    xs: 150,
                                                    sm: 'auto',
                                                  },
                                                }}
                                              >
                                                {variant.variantTitle}
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  display: {
                                                    xs: 'none',
                                                    sm: 'table-cell',
                                                  },
                                                }}
                                              >
                                                {variant.sku || '-'}
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  display: {
                                                    xs: 'none',
                                                    md: 'table-cell',
                                                  },
                                                }}
                                              >
                                                {variant.totalSold}
                                              </TableCell>
                                              <TableCell align="right">
                                                {variant.currentStock}
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  display: {
                                                    xs: 'none',
                                                    sm: 'table-cell',
                                                  },
                                                }}
                                              >
                                                {variant.dailySalesRate.toFixed(
                                                  1,
                                                )}
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  display: {
                                                    xs: 'none',
                                                    lg: 'table-cell',
                                                  },
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    color:
                                                      getDaysToSoldOutColor(
                                                        variant.daysToSoldOut,
                                                      ),
                                                  }}
                                                >
                                                  {variant.daysToSoldOut != null
                                                    ? `${Math.round(variant.daysToSoldOut)} days`
                                                    : 'N/A'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="center">
                                                <FormControl
                                                  size="small"
                                                  sx={{ minWidth: 140 }}
                                                >
                                                  <Select
                                                    value={
                                                      variant.isLowStock
                                                        ? 'to_restock'
                                                        : 'pending'
                                                    }
                                                    onChange={(e) => {
                                                      // Qui potresti aggiungere logica per gestire le varianti
                                                      console.log(
                                                        'Variant action:',
                                                        e.target.value,
                                                        variant.variantId,
                                                      );
                                                    }}
                                                    displayEmpty
                                                    sx={{
                                                      '& .MuiSelect-select': {
                                                        py: 0.5,
                                                        px: 1,
                                                      },
                                                      '& .MuiOutlinedInput-notchedOutline':
                                                        {
                                                          border: 'none',
                                                        },
                                                      '&:hover .MuiOutlinedInput-notchedOutline':
                                                        {
                                                          border:
                                                            '1px solid #ccc',
                                                        },
                                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                        {
                                                          border:
                                                            '1px solid #1976d2',
                                                        },
                                                    }}
                                                  >
                                                    <MenuItem value="pending">
                                                      <Chip
                                                        label="Pending"
                                                        size="small"
                                                        sx={{
                                                          bgcolor: '#e0e0e0',
                                                          color: '#424242',
                                                          fontWeight: 'bold',
                                                          border:
                                                            '1px solid #d5d5d5',
                                                          '& .MuiChip-label': {
                                                            px: 1,
                                                          },
                                                        }}
                                                      />
                                                    </MenuItem>
                                                    <MenuItem value="to_restock">
                                                      <Chip
                                                        label="To Restock"
                                                        size="small"
                                                        sx={{
                                                          bgcolor: '#fff3cd',
                                                          color: '#856404',
                                                          fontWeight: 'bold',
                                                          border:
                                                            '1px solid #ffeaa7',
                                                          '& .MuiChip-label': {
                                                            px: 1,
                                                          },
                                                        }}
                                                      />
                                                    </MenuItem>
                                                    <MenuItem value="restocked">
                                                      <Chip
                                                        label="Restocked"
                                                        size="small"
                                                        sx={{
                                                          bgcolor: '#d4edda',
                                                          color: '#155724',
                                                          fontWeight: 'bold',
                                                          border:
                                                            '1px solid #c3e6cb',
                                                          '& .MuiChip-label': {
                                                            px: 1,
                                                          },
                                                        }}
                                                      />
                                                    </MenuItem>
                                                  </Select>
                                                </FormControl>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                    {!loading && paginatedData.length > 0 ? (
                      <TableRow
                        sx={{
                          bgcolor: palette.background.tableRow,
                          fontWeight: 600,
                        }}
                      >
                        <TableCell
                          sx={{
                            minWidth: { xs: 200, sm: 'auto' },
                            fontWeight: 600,
                          }}
                        >
                          Totals
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat('it-IT', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            useGrouping: true,
                          }).format(
                            paginatedData.reduce(
                              (sum, p) =>
                                sum +
                                (typeof p.totalSold === 'number'
                                  ? p.totalSold
                                  : Number(p.totalSold) || 0),
                              0,
                            ),
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                        >
                          {new Intl.NumberFormat('it-IT', {
                            style: 'currency',
                            currency: 'EUR',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(
                            paginatedData.reduce(
                              (sum, p) =>
                                sum +
                                (typeof p.totalRevenue === 'number' &&
                                Number.isFinite(p.totalRevenue)
                                  ? p.totalRevenue
                                  : 0),
                              0,
                            ),
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat('it-IT', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            useGrouping: true,
                          }).format(
                            paginatedData.reduce(
                              (sum, p) => sum + (Number(p.currentStock) || 0),
                              0,
                            ),
                          )}
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="center" />
                        <TableCell align="center" />
                        <TableCell align="center" />
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'space-between', sm: 'flex-start' },
                  }}
                >
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={sortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    sx={{
                      flex: { sm: 1 },
                      width: { xs: '100%' },
                      overflow: 'visible',
                      '& .MuiTablePagination-actions': { flexShrink: 0 },
                      '& .MuiTablePagination-toolbar': {
                        flexWrap: 'wrap',
                        paddingLeft: { xs: 1 },
                        paddingRight: { xs: 1 },
                      },
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={downloadCSV}
                  disabled={sortedData.length === 0}
                  sx={{
                    bgcolor: palette.primary.main,
                    color: 'black',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    mr: { sm: 2 },
                    borderRadius: 2,
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                    border: '2px solid transparent',
                    '&:hover': {
                      bgcolor: palette.primary.main,
                      opacity: 0.95,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      border: '2px solid #059669',
                    },
                  }}
                >
                  Download CSV
                </Button>
              </Box>
            </Card>
          )}
        </>

        {/* Dialog per modifica status */}
        <Dialog
          open={!!editingProduct}
          onClose={() => setEditingProduct(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit product status</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editStatus}
                  onChange={(e) =>
                    setEditStatus(e.target.value as 'to_restock' | 'restocked')
                  }
                >
                  <MenuItem value="to_restock">To restock</MenuItem>
                  <MenuItem value="restocked">Restocked</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Note"
                multiline
                rows={3}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingProduct(null)}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              sx={{ bgcolor: palette.primary.main }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog rapido: marca come Restocked con data/quantità */}
        <Dialog
          open={restockDialogOpen}
          onClose={() => setRestockDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Mark as restocked</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'grid', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {restockTarget?.productTitle || 'Selected product'}
              </Typography>
              <TextField
                label="Restock date"
                type="date"
                value={restockDate}
                onChange={(e) => setRestockDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Quantity restocked"
                type="number"
                value={restockQuantity}
                onChange={(e) => setRestockQuantity(e.target.value)}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                helperText="Optional. Leave empty if unknown."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestockDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmRestock}
              variant="contained"
              sx={{ bgcolor: palette.primary.main }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* TODO: Abilitare dopo le migrazioni database */}
        {/* Dialog per configurare soglia */}
        <Dialog
          open={showThresholdDialog}
          onClose={() => setShowThresholdDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              ⚙️ Configure Low Stock Threshold
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set the number of days before a product is marked as "To Restock"
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Days to sold out threshold"
              type="number"
              fullWidth
              variant="outlined"
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value) || 30)}
              helperText="Products with fewer days will be marked as 'To Restock'"
              inputProps={{ min: 1, max: 365 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowThresholdDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateThreshold}
              variant="contained"
              sx={{ bgcolor: palette.primary.main }}
            >
              Save Threshold
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menu di filtro per Status */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          PaperProps={{
            sx: {
              minWidth: 150,
              mt: 1,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setStatusFilter('all');
              setFilterAnchorEl(null);
            }}
            selected={statusFilter === 'all'}
          >
            All Status
          </MenuItem>
          <MenuItem
            onClick={() => {
              setStatusFilter('pending');
              setFilterAnchorEl(null);
            }}
            selected={statusFilter === 'pending'}
          >
            Pending
          </MenuItem>
          <MenuItem
            onClick={() => {
              setStatusFilter('to_restock');
              setFilterAnchorEl(null);
            }}
            selected={statusFilter === 'to_restock'}
          >
            To Restock
          </MenuItem>
          <MenuItem
            onClick={() => {
              setStatusFilter('restocked');
              setFilterAnchorEl(null);
            }}
            selected={statusFilter === 'restocked'}
          >
            Restocked
          </MenuItem>
          <MenuItem
            onClick={() => {
              setStatusFilter('skipped');
              setFilterAnchorEl(null);
            }}
            selected={statusFilter === 'skipped'}
          >
            Skipped
          </MenuItem>
        </Menu>

        {/* AI Report Modal */}
        <Dialog
          open={!!aiReport}
          onClose={() => setAiReport(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>AI Inventory Report</DialogTitle>
          <DialogContent>
            {/* Barra confronto YoY deterministica */}
            {comparison && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  background: '#fafafa',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Year-over-Year (same window)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
                  <Typography variant="body2">
                    Current: <strong>{comparison.curr.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Previous:{' '}
                    <strong>{comparison.prev.toLocaleString()}</strong>
                  </Typography>
                  <Chip
                    size="small"
                    color={comparison.isIncrease ? 'success' : 'error'}
                    label={`${comparison.isIncrease ? '↗' : '↘'} ${Math.abs(comparison.delta).toFixed(1)}%`}
                  />
                </Box>
              </Box>
            )}

            {aiReport && <div dangerouslySetInnerHTML={{ __html: aiReport }} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAiReport(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </ContentContainer>
    </InventoryContainer>
  );
};

export default Inventory;
